import { createContext, useContext } from 'react'

import { useHomepageState } from '../../hooks'

import type { HomepageBanner, HomepageBrandCard, HomepageCategoryCard, HomepageProductSlider, HomepagePromoBlock, ReviewCard } from '../../types'
import type { ReactNode } from 'react'

interface HomepageContextValue {
  banners: HomepageBanner[];
  productSliders: HomepageProductSlider[];
  promoBlocks: HomepagePromoBlock[];
  categoryCards: HomepageCategoryCard[];
  brandCards: HomepageBrandCard[];
  isLoading: boolean;
  error: Error | null;
  // Reviews (separate query)
  reviews: ReviewCard[];
  reviewsLoading: boolean;
}

const HomepageContext = createContext<HomepageContextValue | null>(null);

interface HomepageProviderProps {
  children: ReactNode;
}

/**
 * HomepageProvider - THIN provider that calls composition hook
 *
 * React Compiler handles memoization automatically.
 * This provider only composes state from useHomepageState.
 */
export function HomepageProvider({ children }: HomepageProviderProps) {
  const {
    banners,
    productSliders,
    promoBlocks,
    categoryCards,
    brandCards,
    isLoading,
    error,
    reviews,
    reviewsLoading,
  } = useHomepageState();

  const value: HomepageContextValue = {
    banners,
    productSliders,
    promoBlocks,
    categoryCards,
    brandCards,
    isLoading,
    error,
    reviews,
    reviewsLoading,
  };

  return (
    <HomepageContext value={value}>
      {children}
    </HomepageContext>
  );
}

export function useHomepage(): HomepageContextValue {
  const context = useContext(HomepageContext);
  if (!context) {
    throw new Error('useHomepage must be used within HomepageProvider');
  }
  return context;
}
